import { useTranslation } from "react-i18next";
import { Exam, HourglassMedium, CheckCircle, Clock } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import type { TestAssignment } from "@/types/test";

interface Props {
  assignments: TestAssignment[];
  isLoading?: boolean;
}

export function AssignmentSummary({ assignments, isLoading = false }: Props) {
  const { t } = useTranslation();
  const counts = assignments.reduce(
    (acc, a) => {
      acc[a.status] += 1;
      return acc;
    },
    { active: 0, submitted: 0, expired: 0 } as Record<TestAssignment["status"], number>,
  );
  const v = (n: number) => (isLoading ? "..." : String(n));

  return (
    <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <SummaryTile
        icon={Exam}
        iconClass="text-foreground/70"
        label={t("tests.therapist.summary.total")}
        value={v(assignments.length)}
        hint={t("tests.therapist.summary.totalHint")}
      />
      <SummaryTile
        icon={HourglassMedium}
        iconClass="text-primary"
        label={t("tests.therapist.summary.active")}
        value={v(counts.active)}
        hint={t("tests.therapist.summary.activeHint")}
      />
      <SummaryTile
        icon={CheckCircle}
        iconClass="text-emerald-600"
        label={t("tests.therapist.summary.submitted")}
        value={v(counts.submitted)}
        hint={t("tests.therapist.summary.submittedHint")}
      />
      <SummaryTile
        icon={Clock}
        iconClass="text-muted-foreground/60"
        label={t("tests.therapist.summary.expired")}
        value={v(counts.expired)}
        hint={t("tests.therapist.summary.expiredHint")}
      />
    </section>
  );
}

function SummaryTile({
  icon: Icon,
  iconClass,
  label,
  value,
  hint,
}: {
  icon: typeof Exam;
  iconClass: string;
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <div className="rounded-xl border border-border/60 bg-card px-4 py-3 shadow-sm">
      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        <Icon className={cn("size-4", iconClass)} />
        <span>{label}</span>
      </div>
      <p className="mt-1.5 text-2xl font-bold tabular-nums text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
        {value}
      </p>
      <p className="truncate text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}
